import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

interface LoginAlertContextValue {
  /** true enquanto a tarja "você precisa entrar" está visível. */
  visible: boolean
  show: () => void
  hide: () => void
}

const LoginAlertContext = createContext<LoginAlertContextValue | null>(null)

/**
 * Controla a tarja vermelha exibida logo abaixo do cabeçalho quando um visitante
 * tenta marcar uma compra sem estar logado. Fica em contexto porque é disparada
 * de qualquer card, em qualquer página, mas renderizada uma vez só no layout.
 */
export function LoginAlertProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  const hide = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    setVisible(false)
  }, [])

  const show = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    setVisible(true)
    timeoutRef.current = window.setTimeout(() => setVisible(false), 6000)
  }, [])

  const value = useMemo(() => ({ visible, show, hide }), [visible, show, hide])

  return <LoginAlertContext.Provider value={value}>{children}</LoginAlertContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLoginAlert(): LoginAlertContextValue {
  const context = useContext(LoginAlertContext)
  if (!context) throw new Error('useLoginAlert precisa estar dentro de <LoginAlertProvider>')
  return context
}
